// components/fournisseurs/SupplierDetail.tsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { createResource,getResources,deleteResource, updateResource, patchResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface Supplier {
  id: string;
  reference: string;
  raisonSociale: string;
  nif: string;
  contactPrincipal: string;
  telephone: string;
  email: string;
  adresse: string;
  conditionsPaiement: string;
  devise: string;
  delaiMoyenLivraison: number;
  actif: boolean;
  bloque: boolean;
  motif_blocage?: string | null;
  rib?: string;
  dateCreation?: string;
}

interface SupplierStats {
  totalCommandes: number;
  montantTotal: number;
  commandesEnCours: number;
  derniereLivraison: string | null;
  tauxLivraisonTemps: number;
  evaluationQualite: number;
}

interface SupplierDetailProps {
  supplier: Supplier;
  onClose: () => void;
  onEdit: (supplier: Supplier) => void;
  onCreateOrder: (supplier: Supplier) => void;
  onViewHistory: (supplier: Supplier) => void;
}

export default function SupplierDetail({
  supplier: initialSupplier,
  onClose,
  onEdit,
  onCreateOrder,
  onViewHistory,
}: SupplierDetailProps) {
  const [supplier, setSupplier] = useState<Supplier>(initialSupplier);
  const [stats, setStats] = useState<SupplierStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // === CHARGEMENT DES STATS ===
  const fetchStats = useCallback(async () => {
    try {
      setLoadingStats(true);
      const { data } = await getResources(`fournisseurs/${supplier.id}/stats`);
      setStats(data);
    } catch (err) {
      toast.error('Impossible de charger les statistiques');
    } finally {
      setLoadingStats(false);
    }
  }, [supplier.id]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);


// Dans le composant
const [showMotifModal, setShowMotifModal] = useState(false);
const [motifInput, setMotifInput] = useState('');
const [motifError, setMotifError] = useState('');
const motifRef = useRef<HTMLTextAreaElement>(null);

// === NOUVEAU : Modale pour motif ===
const openMotifModal = () => {
  setMotifInput('');
  setMotifError('');
  setShowMotifModal(true);
  setTimeout(() => motifRef.current?.focus(), 100);
};

const confirmDesactivation = async () => {
  if (!motifInput.trim() || motifInput.trim().length < 5) {
    setMotifError('Le motif doit contenir au moins 5 caractères.');
    return;
  }

  try {
    setUpdatingStatus(true);
    await patchResource(
      `fournisseurs/${supplier.id}`,
      { actif: false,
        motif_blocage: motifInput.trim(),
      }
    );

    setSupplier(prev => ({
      ...prev,
      actif: false,
      bloque: true,
      motif_blocage: motifInput.trim(),
    }));

    toast.success('Fournisseur désactivé');
    setShowMotifModal(false);
  } catch (err: any) {
    toast.error(err.response?.data?.message || 'Échec de la désactivation');
  } finally {
    setUpdatingStatus(false);
  }
};

  // === TOGGLE ACTIF / BLOQUÉ ===
  const estActif = supplier.actif === true;

  const handleStatusToggle = async () => {
    if (updatingStatus) return;

    if (estActif) {
      // Désactiver → ouvrir modale
      openMotifModal();
    } else {
      // Réactiver → direct
      try {
        setUpdatingStatus(true);
        await patchResource(`fournisseurs/${supplier.id}`, {
          actif: true,
        });
        setSupplier(prev => ({ ...prev, actif: true, bloque: false, motif_blocage: null }));
        toast.success('Fournisseur réactivé');
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Échec');
      } finally {
        setUpdatingStatus(false);
      }
    }
  };
  
  // === FORMATAGE ===
  const formatPrice = (amount: number) =>
    new Intl.NumberFormat('fr-GN', { style: 'currency', currency: 'GNF', minimumFractionDigits: 0 }).format(amount);

  const formatDate = (date: string | null) => (!date ? 'Aucune' : new Date(date).toLocaleDateString('fr-FR'));

  // === BADGE STATUT (basé sur actif/bloque) ===
  const statusConfig = estActif
    ? { label: 'Actif', bg: 'bg-green-100', text: 'text-green-800', icon: 'ri-check-circle-line' }
    : { label: 'Inactif', bg: 'bg-red-100', text: 'text-red-800', icon: 'ri-error-warning-line' };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <i className="ri-arrow-left-line text-2xl" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{supplier.raisonSociale}</h2>
            <p className="text-sm text-gray-600">Réf: {supplier.reference}</p>
          </div>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          {/* Badge + Toggle */}
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${statusConfig.bg} ${statusConfig.text}`}>
              <i className={`${statusConfig.icon} text-lg`} />
              {statusConfig.label}
              {!estActif && supplier.motif_blocage && (
                <span className="text-xs opacity-80">({supplier.motif_blocage})</span>
              )}
            </span>

            <button
              onClick={handleStatusToggle}
              disabled={updatingStatus}
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${
                estActif
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {updatingStatus ? (
                <i className="ri-loader-4-line animate-spin" />
              ) : estActif ? (
                <>Désactiver</>
              ) : (
                <>Activer</>
              )}
            </button>
          </div>

          {/* Actions principales */}
          <div className="flex gap-2">
            <button
              onClick={() => onEdit(supplier)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
            >
              <i className="ri-edit-line" /> Modifier
            </button>
            <button
              onClick={() => onCreateOrder(supplier)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
            >
              <i className="ri-shopping-cart-line" /> Commande
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Stats rapides */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {loadingStats ? (
            [...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-50 p-4 rounded-lg animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
                <div className="h-8 bg-gray-300 rounded w-16" />
              </div>
            ))
          ) : stats ? (
            <>
              <StatCard icon="ri-shopping-cart-line" label="Total Commandes" value={stats.totalCommandes} color="blue" />
              <StatCard icon="ri-money-dollar-circle-line" label="Montant Total" value={formatPrice(stats.montantTotal)} color="green" />
              <StatCard icon="ri-time-line" label="En Cours" value={stats.commandesEnCours} color="orange" />
              <StatCard icon="ri-truck-line" label="Délai Moyen" value={`${supplier.delaiMoyenLivraison}j`} color="purple" />
            </>
          ) : null}
        </div>

        {/* Détails */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <InfoSection title="Informations Générales" icon="ri-building-line text-blue-600">
            <InfoRow label="Raison Sociale" value={supplier.raisonSociale} />
            <InfoRow label="Référence" value={supplier.reference} />
            <InfoRow label="NIF" value={supplier.nif} />
            <InfoRow label="Contact Principal" value={supplier.contactPrincipal} />
            <InfoRow label="Date Création" value={formatDate(supplier.dateCreation)} />
          </InfoSection>

          <InfoSection title="Coordonnées" icon="ri-map-pin-line text-green-600">
            <div className="py-2 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-600 block mb-1">Adresse</span>
              <span className="text-sm text-gray-900">{supplier.adresse || 'Non renseignée'}</span>
            </div>
            <InfoRow label="Téléphone">
              <a href={`tel:${supplier.telephone}`} className="text-blue-600 hover:underline">{supplier.telephone}</a>
            </InfoRow>
            <InfoRow label="Email">
              <a href={`mailto:${supplier.email}`} className="text-blue-600 hover:underline">{supplier.email}</a>
            </InfoRow>
          </InfoSection>

          <InfoSection title="Conditions Commerciales" icon="ri-money-dollar-circle-line text-purple-600">
            <InfoRow label="Conditions Paiement" value={supplier.conditionsPaiement} />
            <InfoRow label="Devise" value={supplier.devise} />
            <InfoRow label="Délai Moyen" value={`${supplier.delaiMoyenLivraison} jours`} />
            {supplier.rib && <InfoRow label="RIB/IBAN" value={supplier.rib} />}
          </InfoSection>

          <InfoSection title="Performance" icon="ri-bar-chart-line text-orange-600">
            <InfoRow label="Dernière Livraison" value={formatDate(stats?.derniereLivraison)} />
            <InfoRow label="Taux Livraison à Temps" value={stats ? `${stats.tauxLivraisonTemps}%` : '-'} className="text-green-600 font-semibold" />
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-600">Évaluation Qualité</span>
              <div className="flex items-center gap-1">
                <span className="text-yellow-600 font-semibold">{stats?.evaluationQualite?.toFixed(1) ?? '-'}/5</span>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className={`ri-star-${s <= (stats?.evaluationQualite || 0) ? 'fill' : 'line'} text-yellow-400 text-sm`} />
                  ))}
                </div>
              </div>
            </div>
          </InfoSection>
        </div>

        {/* Actions rapides */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions Rapides</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <QuickAction onClick={() => onViewHistory(supplier)} icon="ri-history-line" label="Historique des achats" color="gray" />
            <QuickAction onClick={() => onCreateOrder(supplier)} icon="ri-shopping-cart-line" label="Nouvelle Commande" color="purple" />
            <QuickAction onClick={() => onEdit(supplier)} icon="ri-edit-line" label="Modifier le fournisseur" color="blue" />
          </div>
        </div>
      </div>
      {/* === MODALE MOTIF BLOCAGE === */}
      {showMotifModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                <i className="ri-error-warning-line text-red-600 mr-2" />
                Motif de désactivation
              </h3>
              <button
                onClick={() => setShowMotifModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pourquoi désactivez-vous ce fournisseur ?
              </label>
              <textarea
                ref={motifRef}
                value={motifInput}
                onChange={(e) => {
                  setMotifInput(e.target.value);
                  setMotifError('');
                }}
                placeholder="Ex: Non respect des délais, problème de qualité..."
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm resize-none h-24 ${
                  motifError ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {motifError && <p className="text-xs text-red-600 mt-1">{motifError}</p>}
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowMotifModal(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={confirmDesactivation}
                disabled={updatingStatus}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {updatingStatus ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-check-line" />
                )}
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    
  );
}

// === COMPOSANTS RÉUTILISABLES (inchangés) ===
const StatCard = ({ icon, label, value, color }: { icon: string; label: string; value: string | number; color: string }) => {
  const colors = { blue: 'bg-blue-50 text-blue-600', green: 'bg-green-50 text-green-600', orange: 'bg-orange-50 text-orange-600', purple: 'bg-purple-50 text-purple-600' };
  return (
    <div className={`${colors[color as keyof typeof colors]} p-4 rounded-lg`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <i className={`${icon} text-3xl opacity-80`} />
      </div>
    </div>
  );
};

const InfoSection = ({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) => (
  <div>
    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
      <i className={`${icon} mr-2`} />
      {title}
    </h3>
    <div className="space-y-1 bg-gray-50 rounded-lg p-4">{children}</div>
  </div>
);

const InfoRow = ({ label, value, children, className }: { label: string; value?: string; children?: React.ReactNode; className?: string }) => (
  <div className={`flex justify-between py-2 border-b border-gray-100 last:border-0 ${className || ''}`}>
    <span className="text-sm font-medium text-gray-600">{label}</span>
    <span className="text-sm text-gray-900 text-right">{children || value}</span>
  </div>
);

const QuickAction = ({ onClick, icon, label, color }: { onClick: () => void; icon: string; label: string; color: string }) => {
  const colors = { gray: 'bg-gray-100 hover:bg-gray-200 text-gray-700', purple: 'bg-purple-100 hover:bg-purple-200 text-purple-700', blue: 'bg-blue-100 hover:bg-blue-200 text-blue-700' };
  return (
    <button onClick={onClick} className={`flex items-center justify-center px-4 py-3 rounded-lg transition-colors font-medium ${colors[color as keyof typeof colors]}`}>
      <i className={`${icon} mr-2 text-lg`} />
      {label}
    </button>
  );
};